// ไฟล์: frontend/src/pages/teacher/GrowthReports.js
import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import Chart from 'chart.js/auto';

function TeacherGrowthReports() {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [growthHistory, setGrowthHistory] = useState([]);
  const [growthChart, setGrowthChart] = useState(null);
  const [overviewChart, setOverviewChart] = useState(null);
  const [filterType, setFilterType] = useState('all'); // 'all', 'underweight', 'normal', 'overweight', 'obese'
  
  useEffect(() => {
    fetchClasses();
  }, []);
  
  useEffect(() => {
    // เมื่อเลือกชั้นเรียน ให้โหลดข้อมูลสรุปน้ำหนักและส่วนสูง
    if (selectedClassId) {
      fetchGrowthSummary();
    }
  }, [selectedClassId, filterType]);
  
  const fetchClasses = async () => {
    try {
      setIsLoading(true);
      const response = await api.class.getMyClasses();
      const myClasses = response.data.data || [];
      setClasses(myClasses);
      
      // เลือกชั้นเรียนแรกเป็นค่าเริ่มต้น
      if (myClasses.length > 0) {
        setSelectedClassId(myClasses[0].id);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลชั้นเรียนได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchGrowthSummary = async () => {
    try {
      setIsLoading(true);
      const response = await api.growth.getGrowthSummaryByClass(selectedClassId);
      let studentsData = response.data.data || [];
      
      // เพิ่มค่า BMI และการประเมิน
      studentsData = studentsData.map(student => {
        let bmi = null;
        let bmiStatus = null;
        
        // แปลงข้อมูลให้เป็นตัวเลข
        const weight = student.current_weight ? parseFloat(student.current_weight) : null;
        const height = student.current_height ? parseFloat(student.current_height) : null;
        
        // คำนวณ BMI หากมีทั้งน้ำหนักและส่วนสูง
        if (weight && height) {
          const heightInMeters = height / 100;
          bmi = weight / (heightInMeters * heightInMeters);
          bmi = Math.round(bmi * 10) / 10; // ทศนิยม 1 ตำแหน่ง
          
          // ประเมินสถานะ BMI
          bmiStatus = evaluateBmiStatus(bmi, student.age, student.gender);
        }
        
        return {
          ...student,
          current_weight: weight,
          current_height: height,
          bmi,
          bmiStatus
        };
      });
      
      // กรองตามประเภทที่เลือก
      if (filterType !== 'all') {
        studentsData = studentsData.filter(student => student.bmiStatus === filterType);
      }
      
      setStudents(studentsData);
      
      // สร้างกราฟภาพรวม
      setTimeout(() => {
        createOverviewChart(studentsData);
      }, 300);
    } catch (error) {
      console.error('Error fetching growth summary:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลสรุปการเจริญเติบโตได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchStudentGrowthHistory = async (studentId) => {
    try {
      setIsLoading(true);
      const response = await api.growth.getGrowthByStudent(studentId);
      const historyData = response.data.data.growth_history || [];
      setGrowthHistory(historyData);
      
      // สร้างกราฟเมื่อมีข้อมูล
      if (historyData.length > 0) {
        setTimeout(() => {
          createGrowthChart(historyData);
        }, 300);
      }
    } catch (error) {
      console.error('Error fetching student growth history:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดประวัติการเจริญเติบโตได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleShowHistory = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
    fetchStudentGrowthHistory(student.student_id);
  };
  
  const createGrowthChart = (historyData) => {
    // เตรียมข้อมูลสำหรับกราฟ
    const sortedData = [...historyData].sort((a, b) => new Date(a.record_date) - new Date(b.record_date));
    const dates = sortedData.map(record => new Date(record.record_date).toLocaleDateString('th-TH'));
    const weights = sortedData.map(record => record.weight);
    const heights = sortedData.map(record => record.height);
    
    // ทำลายกราฟเก่า (ถ้ามี)
    if (growthChart) {
      growthChart.destroy();
    }
    
    // สร้างกราฟใหม่
    const ctx = document.getElementById('growthChart');
    if (!ctx) return;
    
    const newChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: dates,
        datasets: [
          {
            label: 'น้ำหนัก (กก.)',
            data: weights,
            backgroundColor: 'rgba(255, 99, 132, 0.2)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 2,
            yAxisID: 'y-weight'
          },
          {
            label: 'ส่วนสูง (ซม.)',
            data: heights,
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 2,
            yAxisID: 'y-height'
          }
        ]
      },
      options: {
        responsive: true,
        scales: {
          'y-weight': {
            type: 'linear',
            position: 'left',
            title: {
              display: true,
              text: 'น้ำหนัก (กก.)'
            }
          },
          'y-height': {
            type: 'linear',
            position: 'right',
            title: {
              display: true,
              text: 'ส่วนสูง (ซม.)'
            },
            grid: {
              drawOnChartArea: false
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: 'กราฟแสดงพัฒนาการน้ำหนักและส่วนสูง'
          }
        }
      }
    });
    
    setGrowthChart(newChart);
  };
  
  const createOverviewChart = (studentsData) => {
    // นับจำนวนนักเรียนในแต่ละกลุ่ม
    const countData = {
      underweight: studentsData.filter(s => s.bmiStatus === 'underweight').length,
      normal: studentsData.filter(s => s.bmiStatus === 'normal').length,
      overweight: studentsData.filter(s => s.bmiStatus === 'overweight').length,
      obese: studentsData.filter(s => s.bmiStatus === 'obese').length,
      noData: studentsData.filter(s => s.bmiStatus === null).length
    };
    
    // ทำลายกราฟเก่า (ถ้ามี)
    if (overviewChart) {
      overviewChart.destroy();
    }
    
    // สร้างกราฟใหม่
    const ctx = document.getElementById('overviewChart');
    if (!ctx) return;
    
    const newChart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: ['น้ำหนักต่ำกว่าเกณฑ์', 'น้ำหนักปกติ', 'น้ำหนักเกิน', 'อ้วน', 'ไม่มีข้อมูล'],
        datasets: [{
          data: [
            countData.underweight,
            countData.normal,
            countData.overweight,
            countData.obese,
            countData.noData
          ],
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)', // น้ำหนักต่ำกว่าเกณฑ์ - น้ำเงิน
            'rgba(75, 192, 192, 0.7)', // น้ำหนักปกติ - เขียวฟ้า
            'rgba(255, 206, 86, 0.7)', // น้ำหนักเกิน - เหลือง
            'rgba(255, 99, 132, 0.7)',  // อ้วน - แดง
            'rgba(201, 203, 207, 0.7)'  // ไม่มีข้อมูล - เทา
          ],
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'bottom',
          },
          title: {
            display: true,
            text: 'สัดส่วนนักเรียนแยกตามเกณฑ์น้ำหนัก'
          }
        }
      }
    });
    
    setOverviewChart(newChart);
  };
  
  // ฟังก์ชันประเมินสถานะ BMI (เกณฑ์อย่างง่าย)
  const evaluateBmiStatus = (bmi, age, gender) => {
    if (!bmi) return null;
    
    // เกณฑ์อย่างง่าย (ในระบบจริงควรใช้เกณฑ์ตามอายุและเพศที่ละเอียดกว่านี้)
    if (bmi < 16) {
      return 'underweight'; // น้ำหนักต่ำกว่าเกณฑ์
    } else if (bmi >= 16 && bmi < 23) {
      return 'normal'; // น้ำหนักปกติ
    } else if (bmi >= 23 && bmi < 27) {
      return 'overweight'; // น้ำหนักเกิน
    } else {
      return 'obese'; // อ้วน
    }
  };
  
  // ฟังก์ชันแสดงสีตามสถานะ BMI
  const getBmiStatusColor = (status) => {
    switch (status) {
      case 'underweight':
        return 'bg-info'; // น้ำเงิน
      case 'normal':
        return 'bg-success'; // เขียว
      case 'overweight':
        return 'bg-warning'; // เหลือง
      case 'obese':
        return 'bg-danger'; // แดง
      default:
        return 'bg-secondary'; // เทา
    }
  };
  
  // ฟังก์ชันแสดงข้อความตามสถานะ BMI
  const getBmiStatusText = (status) => {
    switch (status) {
      case 'underweight':
        return 'น้ำหนักต่ำกว่าเกณฑ์';
      case 'normal':
        return 'น้ำหนักปกติ';
      case 'overweight':
        return 'น้ำหนักเกิน';
      case 'obese':
        return 'อ้วน';
      default:
        return 'ไม่มีข้อมูล';
    }
  };
  
  // ฟังก์ชันสำหรับพิมพ์รายงาน
  const handlePrintReport = () => {
    const printContent = document.getElementById('printableReport');
    const originalContents = document.body.innerHTML;
    
    // เตรียมเนื้อหาสำหรับพิมพ์
    document.body.innerHTML = `
      <div style="padding: 20px;">
        <h1 style="text-align: center;">รายงานน้ำหนักและส่วนสูงนักเรียน</h1>
        <h2 style="text-align: center;">ชั้น ${classes.find(c => c.id === parseInt(selectedClassId))?.class_name || ''}</h2>
        <p style="text-align: center;">วันที่พิมพ์: ${new Date().toLocaleDateString('th-TH')}</p>
        ${printContent.innerHTML}
      </div>
    `;
    
    // พิมพ์
    window.print();
    
    // คืนค่าเดิม
    document.body.innerHTML = originalContents;
    
    // สร้างกราฟใหม่หลังจากการพิมพ์
    setTimeout(() => {
      createOverviewChart(students);
    }, 500);
  };
  
  // ฟังก์ชันส่งออกเป็น CSV
  const handleExportCSV = () => {
    // สร้างข้อมูล CSV
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "รหัสนักเรียน,ชื่อ,นามสกุล,เพศ,อายุ,น้ำหนัก (กก.),ส่วนสูง (ซม.),BMI,สถานะ\n";
    
    students.forEach(student => {
      const row = [
        student.student_code,
        student.first_name,
        student.last_name,
        student.gender === 'male' ? 'ชาย' : 'หญิง',
        student.age || '',
        student.current_weight || '',
        student.current_height || '',
        student.bmi || '',
        getBmiStatusText(student.bmiStatus) || ''
      ];
      
      csvContent += row.join(',') + '\n';
    });
    
    // สร้างลิงก์ดาวน์โหลด
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `รายงานน้ำหนักส่วนสูง_ชั้น${classes.find(c => c.id === parseInt(selectedClassId))?.class_name || ''}_${new Date().toLocaleDateString('th-TH')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div className="wrapper">
      <Header />
      <Sidebar />
      
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">รายงานน้ำหนักและส่วนสูงนักเรียน</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/teacher/dashboard">หน้าหลัก</a>
                  </li>
                  <li className="breadcrumb-item active">รายงานน้ำหนักและส่วนสูง</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <section className="content">
          <div className="container-fluid">
            {isLoading ? (
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            ) : (
              <>
                {/* ตัวเลือกชั้นเรียนและการกรอง */}
                <div className="row">
                  <div className="col-12">
                    <div className="card card-orange">
                      <div className="card-header">
                        <h3 className="card-title">เลือกชั้นเรียนและตัวกรอง</h3>
                      </div>
                      <div className="card-body">
                        {classes.length === 0 ? (
                          <div className="alert alert-warning">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            คุณยังไม่ได้รับมอบหมายให้ดูแลชั้นเรียนใดๆ กรุณาติดต่อผู้ดูแลระบบ
                          </div>
                        ) : (
                          <div className="row">
                            <div className="col-md-4">
                              <div className="form-group">
                                <label>ชั้นเรียน</label>
                                <select
                                  className="form-control"
                                  value={selectedClassId}
                                  onChange={(e) => setSelectedClassId(e.target.value)}
                                >
                                  {classes.map(classItem => (
                                    <option key={classItem.id} value={classItem.id}>
                                      {classItem.class_name} (ห้อง {classItem.room_number})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <label>กรองตามเกณฑ์</label>
                                <select
                                  className="form-control"
                                  value={filterType}
                                  onChange={(e) => setFilterType(e.target.value)}
                                >
                                  <option value="all">ทั้งหมด</option>
                                  <option value="underweight">น้ำหนักต่ำกว่าเกณฑ์</option>
                                  <option value="normal">น้ำหนักปกติ</option>
                                  <option value="overweight">น้ำหนักเกิน</option>
                                  <option value="obese">อ้วน</option>
                                </select>
                              </div>
                            </div>
                            <div className="col-md-4 d-flex align-items-end">
                              <div className="btn-group">
                                <button
                                  type="button"
                                  className="btn btn-success"
                                  onClick={handlePrintReport}
                                >
                                  <i className="fas fa-print mr-1"></i> พิมพ์รายงาน
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={handleExportCSV}
                                >
                                  <i className="fas fa-file-csv mr-1"></i> ส่งออก CSV
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* ภาพรวมกราฟสรุป */}
                <div className="row">
                  <div className="col-md-6">
                    <div className="card card-orange">
                      <div className="card-header">
                        <h3 className="card-title">สรุปภาพรวมชั้นเรียน</h3>
                      </div>
                      <div className="card-body">
                        <canvas id="overviewChart" style={{ height: '300px' }}></canvas>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card card-orange">
                      <div className="card-header">
                        <h3 className="card-title">สรุปข้อมูล</h3>
                      </div>
                      <div className="card-body">
                        <div className="row">
                        <div className="col-md-6 col-sm-6 col-12">
  <div className="info-box bg-info">
    <span className="info-box-icon"><i className="fas fa-weight"></i></span>
    <div className="info-box-content">
      <span className="info-box-text">น้ำหนักเฉลี่ย</span>
      <span className="info-box-number">
        {(() => {
          const studentsWithWeight = students.filter(s => s.current_weight);
          if (studentsWithWeight.length === 0) return 'ไม่มีข้อมูล';
          
          const avgWeight = studentsWithWeight.reduce((sum, student) => 
            sum + parseFloat(student.current_weight), 0) / studentsWithWeight.length;
          
          return isNaN(avgWeight) ? 'ไม่มีข้อมูล' : avgWeight.toFixed(1) + ' กก.';
        })()}
      </span>
    </div>
  </div>
</div>
<div className="col-md-6 col-sm-6 col-12">
  <div className="info-box bg-success">
    <span className="info-box-icon"><i className="fas fa-ruler-vertical"></i></span>
    <div className="info-box-content">
      <span className="info-box-text">ส่วนสูงเฉลี่ย</span>
      <span className="info-box-number">
        {(() => {
          const studentsWithHeight = students.filter(s => s.current_height);
          if (studentsWithHeight.length === 0) return 'ไม่มีข้อมูล';
          
          const avgHeight = studentsWithHeight.reduce((sum, student) => 
            sum + parseFloat(student.current_height), 0) / studentsWithHeight.length;
          
          return isNaN(avgHeight) ? 'ไม่มีข้อมูล' : avgHeight.toFixed(1) + ' ซม.';
        })()}
      </span>
    </div>
  </div>
</div>
                          <div className="col-md-6 col-sm-6 col-12">
                            <div className="info-box bg-warning">
                              <span className="info-box-icon"><i className="fas fa-clipboard-list"></i></span>
                              <div className="info-box-content">
                                <span className="info-box-text">จำนวนนักเรียน</span>
                                <span className="info-box-number">{students.length} คน</span>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6 col-sm-6 col-12">
                            <div className="info-box bg-danger">
                              <span className="info-box-icon"><i className="fas fa-exclamation-triangle"></i></span>
                              <div className="info-box-content">
                                <span className="info-box-text">ต้องเฝ้าระวัง</span>
                                <span className="info-box-number">
                                  {students.filter(s => s.bmiStatus === 'underweight' || s.bmiStatus === 'obese').length} คน
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* รายงานข้อมูลนักเรียน */}
                <div className="row">
                  <div className="col-12">
                    <div className="card card-orange">
                      <div className="card-header">
                        <h3 className="card-title">รายงานน้ำหนักและส่วนสูงนักเรียน</h3>
                      </div>
                      <div className="card-body">
                        <div id="printableReport">
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th style={{ width: '40px' }}>ลำดับ</th>
                                  <th style={{ width: '80px' }}>รหัส</th>
                                  <th>ชื่อ-นามสกุล</th>
                                  <th style={{ width: '60px' }}>เพศ</th>
                                  <th style={{ width: '60px' }}>อายุ</th>
                                  <th style={{ width: '80px' }}>น้ำหนัก</th>
                                  <th style={{ width: '80px' }}>ส่วนสูง</th>
                                  <th style={{ width: '60px' }}>BMI</th>
                                  <th style={{ width: '150px' }}>ผลประเมิน</th>
                                  <th style={{ width: '100px' }} className="no-print">ตัวเลือก</th>
                                </tr>
                              </thead>
                              <tbody>
                                {students.length === 0 ? (
                                  <tr>
                                    <td colSpan="10" className="text-center">
                                      ไม่พบข้อมูลนักเรียน
                                    </td>
                                  </tr>
                                ) : (
                                  students.map((student, index) => (
                                    <tr key={student.student_id}>
                                      <td className="text-center">{index + 1}</td>
                                      <td>{student.student_code}</td>
                                      <td>{student.first_name} {student.last_name}</td>
                                      <td>{student.gender === 'male' ? 'ชาย' : 'หญิง'}</td>
                                      <td>{student.age || '-'}</td>
                                      <td>{student.current_weight ? `${student.current_weight} กก.` : '-'}</td>
                                      <td>{student.current_height ? `${student.current_height} ซม.` : '-'}</td>
                                      <td>{student.bmi || '-'}</td>
                                      <td>
                                        {student.bmiStatus ? (
                                          <span className={`badge ${getBmiStatusColor(student.bmiStatus)}`}>
                                            {getBmiStatusText(student.bmiStatus)}
                                          </span>
                                        ) : '-'}
                                      </td>
                                      <td className="no-print">
                                        <button
                                          className="btn btn-info btn-sm"
                                          onClick={() => handleShowHistory(student)}
                                        >
                                          <i className="fas fa-chart-line"></i> ประวัติ
                                        </button>
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
      
      {/* Modal แสดงประวัติการเจริญเติบโต */}
      {showModal && selectedStudent && (
        <div className="modal fade show" style={{ display: 'block', paddingRight: '15px' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-orange">
                <h5 className="modal-title">
                  ประวัติการเจริญเติบโตของ {selectedStudent.first_name} {selectedStudent.last_name}
                </h5>
                <button type="button" className="close" onClick={() => setShowModal(false)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {isLoading ? (
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                ) : (
                  <>
                    <div className="row mb-3">
                      <div className="col-md-4">
                        <div className="info-box bg-info">
                          <span className="info-box-icon">
                            <i className="fas fa-weight"></i>
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text">น้ำหนักปัจจุบัน</span>
                            <span className="info-box-number">
                              {selectedStudent.current_weight ? `${selectedStudent.current_weight} กก.` : 'ไม่มีข้อมูล'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="info-box bg-success">
                          <span className="info-box-icon">
                            <i className="fas fa-ruler-vertical"></i>
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text">ส่วนสูงปัจจุบัน</span>
                            <span className="info-box-number">
                              {selectedStudent.current_height ? `${selectedStudent.current_height} ซม.` : 'ไม่มีข้อมูล'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className={`info-box ${getBmiStatusColor(selectedStudent.bmiStatus)}`}>
                          <span className="info-box-icon">
                            <i className="fas fa-calculator"></i>
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text">BMI</span>
                            <span className="info-box-number">
                              {selectedStudent.bmi ? `${selectedStudent.bmi} (${getBmiStatusText(selectedStudent.bmiStatus)})` : 'ไม่มีข้อมูล'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* กราฟพัฒนาการน้ำหนักและส่วนสูง */}
                    <div className="chart-container" style={{ height: '300px' }}>
                      <canvas id="growthChart"></canvas>
                    </div>
                    
                    <h5 className="mt-4">ประวัติการวัดน้ำหนักและส่วนสูง</h5>
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>วันที่</th>
                            <th>น้ำหนัก (กก.)</th>
                            <th>ส่วนสูง (ซม.)</th>
                            <th>BMI</th>
                            <th>บันทึกโดย</th>
                            <th>หมายเหตุ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {growthHistory.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="text-center">
                                ไม่พบประวัติการเจริญเติบโต
                              </td>
                            </tr>
                          ) : (
                            growthHistory.map((record) => {
                              // คำนวณ BMI
                              let bmi = null;
                              if (record.weight && record.height) {
                                const heightInMeters = record.height / 100;
                                bmi = record.weight / (heightInMeters * heightInMeters);
                                bmi = Math.round(bmi * 10) / 10; // ทศนิยม 1 ตำแหน่ง
                              }
                              
                              return (
                                <tr key={record.id}>
                                  <td>
                                    {new Date(record.record_date).toLocaleDateString('th-TH')}
                                  </td>
                                  <td>{record.weight} กก.</td>
                                  <td>{record.height} ซม.</td>
                                  <td>{bmi || '-'}</td>
                                  <td>{record.recorded_by_name}</td>
                                  <td>{record.note || '-'}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  ปิด
                </button>
              </div>
            </div>
          </div>
          <div className="modal- fade show"></div>
        </div>
      )}
      
      {/* CSS สำหรับการพิมพ์ */}
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            body {
              font-family: 'Kodchasan', sans-serif;
              padding: 20px;
            }
            .card {
              border: none !important;
              box-shadow: none !important;
            }
            .card-header {
              background-color: #f8f9fa !important;
              color: #000 !important;
              padding: 10px !important;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            table, th, td {
              border: 1px solid #000;
            }
            th, td {
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f8f9fa;
            }
          }
        `}
      </style>
      
      <Footer />
    </div>
  );
}

export default TeacherGrowthReports;